// redux store enhancer that mirrors the session slices into localStorage.
// replaces redux-localstorage with one rule it could not express: view-as state
// is never persisted. localStorage is shared by every tab of the origin and
// view-as opens in a new tab, so persisting it made the impersonated profile
// leak into the admin's other tabs on their next reload. the url (?va=) is the
// only source of truth for view-as; redux is just its cache

export const PERSIST_KEY = 'redux';

/**
 * strips view-as traces from a user slice read back from storage
 * @param user - persisted state.user, possibly written by an older build mid view-as
 */
export function sanitizeHydratedUser(user) {
  if (!user || typeof user !== 'object') return user;

  const wasViewingAs = Boolean(user.viewAsAgent);
  const realId = user.zohoData?.id;
  const next = { ...user, viewAsAgent: null };

  if (realId && next.zohoId !== realId) next.zohoId = realId;
  // a slice written mid view-as holds the impersonated profile; the bootstrap refetches the real one
  next.needsRefresh = wasViewingAs;
  return next;
}

/**
 * reads and sanitizes the persisted slices; undefined when nothing usable is stored
 * @param storage - Storage-like object with getItem
 * @param key - storage key
 */
export function readPersistedState(storage, key = PERSIST_KEY) {
  try {
    const raw = storage?.getItem(key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return undefined;
    if (parsed.user) parsed.user = sanitizeHydratedUser(parsed.user);
    return parsed;
  } catch (error) {
    console.warn('failed to read persisted state', error);
    return undefined;
  }
}

/**
 * picks what gets written to storage; null means "write nothing this time"
 * @param state - full redux state
 */
export function toPersistedState(state) {
  const user = state?.user;
  // the view-as tab must not overwrite what the admin's own tabs rely on
  if (user?.viewAsAgent) return null;

  const persistedUser = { ...(user ?? {}) };
  // hydration-only flag, must not survive a reload
  delete persistedUser.needsRefresh;
  return {
    authentication: state?.authentication,
    user: { ...persistedUser, viewAsAgent: null }
  };
}

/**
 * store enhancer: hydrates from storage on creation and mirrors the session
 * slices on every change, skipping writes while view-as is active
 * @param storage - Storage-like object, defaults to window.localStorage
 * @param key - storage key, defaults to the legacy redux-localstorage key so live sessions survive
 */
export default function persistUserState({
  storage = globalThis.localStorage,
  key = PERSIST_KEY
} = {}) {
  return next => (reducer, preloadedState, enhancer) => {
    if (typeof preloadedState === 'function' && enhancer === undefined) {
      enhancer = preloadedState;
      preloadedState = undefined;
    }

    const persisted = readPersistedState(storage, key);
    const initialState = persisted
      ? { ...preloadedState, ...persisted }
      : preloadedState;

    const store = next(reducer, initialState, enhancer);

    store.subscribe(() => {
      const subset = toPersistedState(store.getState());
      if (!subset || !storage) return;
      try {
        storage.setItem(key, JSON.stringify(subset));
      } catch (error) {
        console.warn('unable to persist state', error);
      }
    });

    return store;
  };
}
